import React from 'react';

const CommunityGuidelines: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Community Guidelines</h1>
        <p className="page-subtitle">Creating a positive and inclusive environment for all writers</p>
        <p className="last-updated">Last updated: January 15, 2025</p>
      </div>

      <div className="page-content legal-content">
        <section className="content-section">
          <h2>1. Our Community Vision</h2>
          <p>
            Flow is built on the belief that every voice deserves to be heard. We strive to create a
            welcoming, inclusive, and supportive community where writers of all backgrounds, skill levels,
            and perspectives can share their stories, learn from each other, and grow together.
          </p>
        </section>

        <section className="content-section">
          <h2>2. Core Principles</h2>

          <h3>2.1 Respect and Kindness</h3>
          <p>
            Treat all community members with respect and kindness. We believe in constructive dialogue
            and supporting fellow writers, even when we disagree.
          </p>

          <h3>2.2 Inclusivity</h3>
          <p>
            Flow is a place for everyone. We welcome writers from all backgrounds, identities,
            experiences, and skill levels. Discrimination, harassment, or hate speech will not be tolerated.
          </p>

          <h3>2.3 Authenticity</h3>
          <p>
            Be genuine in your interactions. Share your authentic voice and respect the authentic voices
            of others. Misrepresentation or impersonation undermines trust in our community.
          </p>

          <h3>2.4 Quality and Originality</h3>
          <p>
            Strive to create and share original, thoughtful content. While we encourage diverse writing
            styles and topics, we expect content to be genuine and respectful to readers.
          </p>
        </section>

        <section className="content-section">
          <h2>3. Content Guidelines</h2>

          <h3>3.1 Original Content</h3>
          <p>
            Share only content that you have created or have permission to publish. Plagiarism,
            copyright infringement, and unauthorized use of others' work is not allowed.
          </p>

          <h3>3.2 Appropriate Content</h3>
          <p>Content shared on Flow should be:</p>
          <ul>
            <li>Legal and compliant with applicable laws</li>
            <li>Respectful to all community members</li>
            <li>Free from hate speech, harassment, or discrimination</li>
            <li>Non-violent and not promoting harmful activities</li>
            <li>Appropriate for a diverse, global audience</li>
          </ul>

          <h3>3.3 Prohibited Content</h3>
          <p>The following types of content are not allowed on Flow:</p>
          <ul>
            <li>Hate speech targeting individuals or groups based on race, religion, gender, sexual orientation, disability, or other protected characteristics</li>
            <li>Harassment, bullying, or targeted attacks against individuals</li>
            <li>Content promoting violence, illegal activities, or self-harm</li>
            <li>Spam, excessive self-promotion, or misleading content</li>
            <li>Adult content or sexually explicit material</li>
            <li>Content that infringes on intellectual property rights</li>
            <li>Doxxing or sharing private information without consent</li>
            <li>Misinformation or deliberately false information presented as fact</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>4. Community Interaction</h2>

          <h3>4.1 Constructive Feedback</h3>
          <p>
            When providing feedback on others' work, focus on being constructive, specific, and helpful.
            Criticism should be about the work, not the person, and should aim to help the writer improve.
          </p>

          <h3>4.2 Respectful Disagreement</h3>
          <p>
            It's natural to have different opinions and perspectives. When you disagree with someone,
            express your views respectfully and focus on the ideas rather than attacking the person.
          </p>

          <h3>4.3 Support and Encouragement</h3>
          <p>
            Celebrate the successes of fellow community members and offer encouragement to those who
            are struggling. A supportive word can make a significant difference to someone's writing journey.
          </p>

          <h3>4.4 Professional Conduct</h3>
          <p>
            Maintain professional standards in all interactions. This includes being reliable in
            collaborations, respecting deadlines, and communicating clearly and respectfully.
          </p>
        </section>

        <section className="content-section">
          <h2>5. Privacy and Safety</h2>

          <h3>5.1 Personal Information</h3>
          <p>
            Protect your privacy and respect others' privacy. Don't share personal information such as
            addresses, phone numbers, or financial information publicly on the platform.
          </p>

          <h3>5.2 Safe Collaboration</h3>
          <p>
            When collaborating with other writers, establish clear boundaries and expectations.
            If you feel uncomfortable or unsafe in any interaction, please report it to our team.
          </p>

          <h3>5.3 Reporting Concerns</h3>
          <p>
            If you encounter content or behavior that violates these guidelines, please report it
            promptly. We rely on our community to help maintain a safe and positive environment.
          </p>
        </section>

        <section className="content-section">
          <h2>6. Intellectual Property</h2>

          <h3>6.1 Respecting Copyright</h3>
          <p>
            Only publish content that you own or have permission to use. When citing or referencing
            others' work, provide proper attribution and follow fair use guidelines.
          </p>

          <h3>6.2 Your Rights</h3>
          <p>
            You retain ownership of all content you create and publish on Flow. By publishing on our
            platform, you grant us permission to display and distribute your content as part of the service.
          </p>

          <h3>6.3 Handling Infringement</h3>
          <p>
            If you believe your intellectual property rights have been violated, please contact us
            immediately with details of the infringement.
          </p>
        </section>

        <section className="content-section">
          <h2>7. Account Responsibility</h2>

          <h3>7.1 Account Security</h3>
          <p>
            Keep your account credentials secure and don't share them with others. You are responsible
            for all activity that occurs under your account.
          </p>

          <h3>7.2 Accurate Information</h3>
          <p>
            Provide accurate information in your profile and maintain it up to date. Misrepresenting
            your identity or qualifications undermines trust in our community.
          </p>

          <h3>7.3 One Account Per Person</h3>
          <p>
            Each person should maintain only one active account. Multiple accounts for the purpose of
            manipulation, spam, or circumventing restrictions are not allowed.
          </p>
        </section>

        <section className="content-section">
          <h2>8. Enforcement and Consequences</h2>

          <h3>8.1 Moderation Approach</h3>
          <p>
            Our moderation team reviews reported content and behavior. We aim to be fair and consistent
            in our enforcement while considering context and intent.
          </p>

          <h3>8.2 Possible Actions</h3>
          <p>
            Violations of these guidelines may result in:
          </p>
          <ul>
            <li>Content removal or editing requests</li>
            <li>Warnings or educational guidance</li>
            <li>Temporary restrictions on account features</li>
            <li>Temporary suspension of account access</li>
            <li>Permanent account termination for severe or repeated violations</li>
          </ul>

          <h3>8.3 Appeal Process</h3>
          <p>
            If you believe a moderation action was taken in error, you can appeal the decision by
            contacting our support team with a detailed explanation of your concerns.
          </p>
        </section>

        <section className="content-section">
          <h2>9. Promoting Positive Engagement</h2>

          <h3>9.1 Best Practices</h3>
          <p>
            To contribute positively to our community:
          </p>
          <ul>
            <li>Read and engage thoughtfully with others' content</li>
            <li>Provide meaningful feedback and encouragement</li>
            <li>Share your knowledge and experience generously</li>
            <li>Ask questions and be open to learning</li>
            <li>Celebrate diversity in writing styles and perspectives</li>
            <li>Be patient with new community members</li>
          </ul>

          <h3>9.2 Building Connections</h3>
          <p>
            Flow is not just a platform for publishing—it's a place to build meaningful connections
            with other writers. Engage authentically, support others' growth, and contribute to the
            collective wisdom of our community.
          </p>
        </section>

        <section className="content-section">
          <h2>10. Guidelines Evolution</h2>
          <p>
            These guidelines may evolve as our community grows and changes. We'll notify the community
            of significant updates and welcome feedback on how we can better serve writers and maintain
            a positive environment.
          </p>
        </section>

        <section className="content-section">
          <h2>11. Getting Help</h2>
          <p>
            If you have questions about these guidelines, need help resolving a conflict, or want to
            report a concern, please don't hesitate to reach out:
          </p>
          <ul>
            <li>Email: community@flow-writing.com</li>
            <li>Through our <a href="/contact">Contact Us</a> page</li>
            <li>Use the report feature on content or profiles</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>12. Thank You</h2>
          <p>
            Thank you for being part of the Flow community. By following these guidelines, you help
            create an environment where all writers can thrive, share their voices, and grow together.
            Your contributions make Flow a better place for everyone.
          </p>
        </section>
      </div>
    </div>
  );
};

export default CommunityGuidelines;